import { useCallback, useState } from "react";
import { pluralizeWithCount } from "@common/helpers/string";
import type { WorkItemEdit } from "@api/workItem.api";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { useOnDemandModal } from "@components/tasks/useOnDemandModal";

const getQuestion = (itemCount: number) =>
    itemCount > 1
        ? `Open tasks with due dates based on Work start will move to match the template in ${pluralizeWithCount("work item", itemCount)}, including due dates updated by hand.`
        : "Open tasks with due dates based on Work start will move to match the template, including due dates updated by hand.";

interface PendingWorkStartChange {
    itemCount: number;
    save: () => void;
}

interface SaveEditArgs {
    edit: WorkItemEdit;
    save: () => void;
    /** How many work items `save` changes; @default 1 */
    itemCount?: number;
}

/**
 * Saves work item edits, asking first when one moves Work start: the backend
 * then moves open tasks' due dates that follow Work start, even ones updated
 * by hand. Other edits save right away. `confirmationModal` is the one modal
 * to render; it keeps the last request after closing so it can animate out
 * with the same copy. Cancel saves nothing.
 */
export const useConfirmWorkStartChange = () => {
    const [pending, setPending] = useState<PendingWorkStartChange | null>(null);
    const { mountedSubject, isOpen, onExited } = useOnDemandModal(pending);
    const saveEdit = useCallback(
        ({ edit, save, itemCount = 1 }: SaveEditArgs) => {
            if (edit.field === "workStartDate") {
                setPending({ itemCount, save });
            } else {
                save();
            }
        },
        [],
    );
    const dismiss = () => setPending(null);

    return {
        saveEdit,
        confirmationModal: mountedSubject && (
            <ConfirmationModal
                show={isOpen}
                onExited={onExited}
                title="Update work start?"
                question={getQuestion(mountedSubject.itemCount)}
                yes="Update work start"
                no="Cancel"
                onConfirm={() => {
                    dismiss();
                    mountedSubject.save();
                }}
                onReject={dismiss}
                onHide={dismiss}
            />
        ),
    };
};
